import { Panel } from '@app/features/dashboard/components';
import { NoteFlagFilter, type NoteFlag } from '@app/features/visits/noteFlag';
import { Button } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import NoteComposer from './NoteComposer';
import NotesTimeline from './NotesTimeline';
import { NOTES_PAGE_SIZE, useClientNotes, useMarkNotesRead } from './useNotes';

type ClientNotesPanelProps = {
  clientId: number;
  className?: string;
  bodyMaxHeight?: number;
};

/**
 * The client's whole note stream — the office's, the caregivers', the doctors' — and
 * a box to add to it.
 *
 * Whole is the point. The card already shows caregiver notes visit by visit; this is
 * the same client read as one conversation, so a standing instruction written last
 * March sits in the same list as what a caregiver wrote on Tuesday.
 *
 * The priority filter goes to the server, so the count beside the title is the
 * server's count of the filter in force and the empty state means what it says. A
 * long stream arrives a page at a time, and the panel says how much of it is on
 * screen rather than showing a number it is not displaying.
 */
const ClientNotesPanel: React.FC<ClientNotesPanelProps> = ({ clientId, className, bodyMaxHeight = 420 }) => {
  const { t } = useTranslation();
  const [flags, setFlags] = useState<NoteFlag[]>([]);
  const [limit, setLimit] = useState(NOTES_PAGE_SIZE);

  const flagKey = flags.join(',');

  // A different filter is a different question, asked from the first page again.
  useEffect(() => {
    setLimit(NOTES_PAGE_SIZE);
  }, [flagKey]);

  const { notes, notesTotal, hasMoreNotes, areNotesLoading, areNotesFetching, hasNotesError } = useClientNotes(
    clientId,
    flags,
    limit
  );

  // Reading the panel is what marks its notes read, so only what the panel actually
  // rendered is marked. The key is the ids themselves, so a language switch or a
  // re-render does not re-post, and a new note does.
  const unreadIds = useMemo(() => notes.filter((note) => !note.isRead).map((note) => note.noteId), [notes]);
  const unreadKey = unreadIds.join(',');

  const { markRead } = useMarkNotesRead();

  useEffect(() => {
    if (unreadKey.length === 0) return;
    markRead(unreadKey.split(',').map(Number));
    // `markRead` is a stable mutate function; the ids are the whole of the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadKey]);

  const unreadCount = unreadIds.length;
  // Neither number means anything before the first answer arrives or after one failed.
  const showsCounts = !areNotesLoading && !hasNotesError;
  const isTruncated = notes.length < notesTotal;

  return (
    <Panel
      bodyHeight={bodyMaxHeight}
      title={t('notes-client-panel-title')}
      className={className}
      pad="p-0"
      extra={
        <div className="flex items-center gap-3">
          <NoteFlagFilter value={flags} onChange={setFlags} />
          {showsCounts && unreadCount > 0 && (
            <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700">
              {t('notes-unread-count', { count: unreadCount })}
            </span>
          )}
          {showsCounts && (
            <span className="text-[12px] tabular-nums text-slate-400">
              {isTruncated
                ? t('notes-count-truncated', { shown: notes.length, total: notesTotal })
                : t('notes-count', { count: notesTotal })}
            </span>
          )}
        </div>
      }
    >
      <NotesTimeline
        notes={notes}
        isLoading={areNotesLoading}
        hasError={hasNotesError}
        emptyKey={flags.length > 0 ? 'notes-empty-flagged' : 'notes-empty'}
        fill
      />

      {/* Under the list, where the reader runs out of notes: the rest has to be
          reachable, not merely announced. */}
      {showsCounts && hasMoreNotes && (
        <div className="border-0 border-t border-slate-100 px-3 py-2 text-center">
          <Button
            type="link"
            size="small"
            loading={areNotesFetching}
            onClick={() => setLimit((current) => current + NOTES_PAGE_SIZE)}
          >
            {t('notes-load-more')}
          </Button>
        </div>
      )}

      {!hasNotesError && <NoteComposer clientId={clientId} />}
    </Panel>
  );
};

export default ClientNotesPanel;
